import { categoriesNlToEn } from "@config/urls"
import { ChoicesRelatedCombination } from "@generated"
import { getDynamicSeoDescription } from "@lib/get-dynamic-seo-description"
import { getGraphQLClient } from "@lib/graphql-client"
import {
  getNormalizedRelatedCombinations,
  NormalizedRelatedCombination,
} from "@lib/hooks/helpers/get-normalized-related-combinations"
import { ErrorPage } from "@screens/error"
import Product from "@screens/product"
import { NextPage } from "next"

type Props = {
  pid?: string
  relatedCombinationId?: string
  uid?: string
  statusCode?: number
  relatedCombination?: NormalizedRelatedCombination
  description?: string | null
}

const { sdk } = getGraphQLClient()

const ProductDetailPage: NextPage<Props> = ({
  pid,
  uid,
  statusCode,
  relatedCombination,
  relatedCombinationId,
  description,
}) => {
  if (statusCode) {
    return <ErrorPage statusCode={statusCode} />
  }

  if (!relatedCombination || !uid || !pid) {
    return <ErrorPage statusCode={404} />
  }

  return (
    <Product
      uid={uid}
      pid={pid}
      relatedCombinationId={relatedCombinationId}
      productType="articleCombination"
      relatedCombination={relatedCombination}
      articlesId={relatedCombination.combination.articles.map(
        (article) => article.id
      )}
      lang={"nl-nl"}
      description={description}
    />
  )
}

ProductDetailPage.getInitialProps = async ({ query, lang }) => {
  const uid =
    lang === "nl"
      ? categoriesNlToEn[query["uid"] as string]
      : (query.uid as string)
  const pid = query["pid"] as string
  const pidParts = pid.split("-")
  const id = pidParts[pidParts.length - 1]

  // We want the old routes to articleCombinations to work, so we need to check if we're dealing with a relatedCombination here
  // If we do, we should do a different call to the API
  const isRelatedCombination = pidParts[0] === "c"
  const relatedCombinationId = isRelatedCombination ? pidParts[1] : undefined

  // const attributes = getAttributesMap(data.article)
  // https://stackoverflow.com/questions/22910428/mustache-globally-disable-html-escaping/23057003#23057003
  try {
    if (relatedCombinationId) {
      const result = await sdk.RelatedCombination({
        id: relatedCombinationId,
      })
      if (!result || !result.choicesRelatedCombination) {
        return { statusCode: 404 }
      }

      const relatedCombination = getNormalizedRelatedCombinations(
        [result.choicesRelatedCombination as ChoicesRelatedCombination],
        id
      )[0]

      const description = await getDynamicSeoDescription({
        item: relatedCombination.combination,
        urlSlug: uid,
        lang,
      })

      return {
        relatedCombination,
        relatedCombinationId,
        uid,
        pid: id,
        description,
      }
    } else {
      const result = await sdk.ArticleCombination({
        id,
      })

      if (!result || !result.choicesArticleCombination) {
        return { statusCode: 404 }
      }
      const description = await getDynamicSeoDescription({
        item: result.choicesArticleCombination,
        urlSlug: uid,
        lang,
      })

      return {
        // Mock relatedCombination to make sure old routes still work
        relatedCombination: {
          combination: result.choicesArticleCombination,
          articles: [],
        } as any as NormalizedRelatedCombination,
        uid,
        pid: id,
        description,
      }
    }
  } catch (error) {
    return {
      statusCode: 500,
    }
  }
}

export default ProductDetailPage
